import React, { Component } from 'react';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';
import PDFCourseContent from './components/courseContent/PDFCourseContent';
import VideoContent from './components/courseContent/VideoContent';
import Question from './Question';
import LoadingSpinner from './LoadingSpinner';
import { trackTrainingEvent } from './trainingTelemetry';
import { zapiszPostep, odczytajPostep, wyczyscPostep } from './scenarioProgress';

// Phases within a single scenario step.
const PHASE_MATERIAL = 'material';
const PHASE_QUESTIONS = 'questions';
const PHASE_STEP_RESULT = 'step_result'; // per-step summary — shown after EVERY step, pass or fail
const PHASE_POST_TEST_MATERIAL = 'post_test_material';
const PHASE_DONE = 'done'; // final thank-you modal after submit-scenario

/**
 * Sequential "step-by-step scenario" training player.
 *
 * Rendered by Training.js instead of the plain materials -> Exam flow whenever
 * the backend returns a non-empty `courseData.scenario`. Walks through
 * `scenario.steps` in order: material fragment (bounded PDF pages / video
 * seconds) -> mini test (subset of questions) -> POST scenario-step. A failed
 * step is retried in place; a passed step advances the index. After the last
 * step, an optional `postTestMaterial` is shown, then POST submit-scenario
 * finalizes the whole training (mirrors Exam.js's finish-exam result screen).
 */
class ScenarioTraining extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stepIndex: 0,
      phase: PHASE_MATERIAL,
      currentQuestionIndex: 0,
      // answers for the step currently in progress: { questionId: answerId }
      currentStepAnswers: {},
      // collected across ALL steps once each is passed: { "0": {...}, "1": {...} }
      answersByStep: {},
      isLoading: false,
      error: null,
      // per-step result summary shown after every step, pass or fail:
      // { passed: bool, message, data: { points, requiredQuestionsCount } }
      stepResult: null,
      showThankYouModal: false,
      modalMessage: '',
      isPassed: false,
      nextTraining: null,
      // Postep z poprzedniego podejscia — pokazujemy pytanie „kontynuowac?" ZANIM
      // cokolwiek wznowimy, zeby uzytkownik wiedzial, ze nie zaczyna od zera.
      wznowienie: null
    };
  }

  componentDidMount() {
    const { userId, trainingId } = this.props;
    const zapisany = odczytajPostep(userId, trainingId);
    if (zapisany && zapisany.stepIndex < this.getSteps().length) {
      this.setState({ wznowienie: zapisany });
      trackTrainingEvent('scenario_resume_offered', 'scenario-resume-offered', 'Scenario resume offered', {
        trainingId,
        stepIndex: zapisany.stepIndex,
      });
    }
    // Zamkniecie karty w trakcie szkolenia nie daje zaliczenia — przegladarka
    // pokaze wlasne ostrzezenie, zanim uzytkownik straci sesje.
    window.addEventListener('beforeunload', this.ostrzezPrzedWyjsciem);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.ostrzezPrzedWyjsciem);
  }

  ostrzezPrzedWyjsciem = (event) => {
    if (this.state.phase === PHASE_DONE || this.props.isPreview) {
      return undefined;
    }
    event.preventDefault();
    // Nowoczesne przegladarki i tak pokazuja wlasny komunikat, ale returnValue
    // jest nadal wymagane, zeby okno w ogole sie pojawilo.
    event.returnValue = '';
    return '';
  };

  kontynuujOdZapisanego = () => {
    const { wznowienie } = this.state;
    trackTrainingEvent('scenario_resume_accepted', 'scenario-resume-accepted', 'Scenario resumed', {
      trainingId: this.props.trainingId,
      stepIndex: wznowienie.stepIndex,
    });
    this.setState({
      stepIndex: wznowienie.stepIndex,
      answersByStep: wznowienie.answersByStep,
      phase: PHASE_MATERIAL,
      currentQuestionIndex: 0,
      currentStepAnswers: {},
      wznowienie: null,
    });
  };

  zacznijOdPoczatku = () => {
    trackTrainingEvent('scenario_resume_declined', 'scenario-resume-declined', 'Scenario restarted from scratch', {
      trainingId: this.props.trainingId,
    });
    wyczyscPostep(this.props.userId, this.props.trainingId);
    this.setState({ wznowienie: null });
  };

  getSteps = () => {
    const { scenario } = this.props;
    return (scenario && Array.isArray(scenario.steps)) ? scenario.steps : [];
  };

  getCurrentStep = () => {
    const steps = this.getSteps();
    return steps[this.state.stepIndex] || null;
  };

  // Called when the material fragment for the current step has been fully
  // consumed (last bounded PDF page reached / video fragment end reached).
  handleMaterialDone = () => {
    const step = this.getCurrentStep();
    const hasQuestions = step && Array.isArray(step.questions) && step.questions.length > 0;
    trackTrainingEvent('scenario_material_done', 'scenario-material', 'Scenario material completed', {
      trainingId: this.props.trainingId,
      stepIndex: this.state.stepIndex,
      hasQuestions,
    });
    if (hasQuestions) {
      this.setState({ phase: PHASE_QUESTIONS, currentQuestionIndex: 0, currentStepAnswers: {} });
    } else {
      // Step with no questions (requiredCorrect trivially met) - submit empty answers.
      this.submitCurrentStep({});
    }
  };

  handleAnswerSubmit = (answerId, questionId) => {
    const step = this.getCurrentStep();
    const updatedAnswers = { ...this.state.currentStepAnswers, [questionId]: answerId };
    const nextIndex = this.state.currentQuestionIndex + 1;
    trackTrainingEvent('scenario_answer_selected', 'scenario-answer', 'Scenario answer selected', {
      trainingId: this.props.trainingId,
      stepIndex: this.state.stepIndex,
      questionId,
      answerId,
      questionIndex: this.state.currentQuestionIndex,
    });

    if (nextIndex >= step.questions.length) {
      this.setState({ currentStepAnswers: updatedAnswers }, () => {
        this.submitCurrentStep(updatedAnswers);
      });
    } else {
      this.setState({ currentStepAnswers: updatedAnswers, currentQuestionIndex: nextIndex });
    }
  };

  submitCurrentStep = async (answers) => {
    const { trainingId, userId } = this.props;
    const { stepIndex } = this.state;
    trackTrainingEvent('scenario_step_submit', 'scenario-step-submit', 'Scenario step submitted', {
      trainingId,
      stepIndex,
      answersCount: Object.keys(answers).length,
    });
    this.setState({ isLoading: true, error: null });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}scenario-step/${trainingId}`,
        // `userId` pozwala serwerowi odnotowac, dokad ta osoba doszla. Bez tego podejscie
        // porzucone w polowie nie zostawia sladu w bazie i BHP nie ma jak zobaczyc, ze
        // ktos zaczal szkolenie i go nie skonczyl. W podgladzie autora userId jest puste
        // — backend traktuje je jako opcjonalne i wtedy niczego nie zapisuje.
        { stepIndex, answers, userId: userId ?? null }
      );
      const { error, message, data } = response.data;
      const passed = !error;
      const steps = this.getSteps();
      const isLastStep = stepIndex >= steps.length - 1;

      trackTrainingEvent('scenario_step_result', 'scenario-step-result',
        passed ? 'Scenario step passed' : 'Scenario step failed', {
          trainingId,
          stepIndex,
          passed,
          message,
          isLastStep,
        });

      // Record the answers only once the step is passed — a failed step is
      // retried in place and its answers are re-submitted from scratch.
      const answersByStep = passed
        ? { ...this.state.answersByStep, [String(stepIndex)]: answers }
        : this.state.answersByStep;

      // Every step — pass or fail — shows a summary with its points before
      // moving on. A passed step used to advance silently, so the learner
      // never saw how they scored; only failures got any feedback.
      this.setState({
        answersByStep,
        isLoading: false,
        phase: PHASE_STEP_RESULT,
        stepResult: { passed, message, data }
      });
    } catch (err) {
      trackTrainingEvent('scenario_step_error', 'scenario-step-error', 'Scenario step error', {
        trainingId,
        stepIndex,
        message: err.message,
        status: err.response?.status || null,
      });
      this.setState({ isLoading: false, error: err });
    }
  };

  handleRetryStep = () => {
    trackTrainingEvent('scenario_step_retry', 'scenario-step-retry', 'Scenario step retry', {
      trainingId: this.props.trainingId,
      stepIndex: this.state.stepIndex,
    });
    this.setState({
      phase: PHASE_MATERIAL,
      currentQuestionIndex: 0,
      currentStepAnswers: {},
      stepResult: null
    });
  };

  handleStepResultContinue = () => {
    const { stepIndex, answersByStep } = this.state;
    const steps = this.getSteps();
    const isLastStep = stepIndex >= steps.length - 1;

    if (isLastStep) {
      this.setState({ stepResult: null }, () => {
        this.advanceAfterLastStep();
      });
    } else {
      // Postep zapisujemy w momencie PRZEJSCIA do kolejnego etapu, nie przy samym
      // zaliczeniu — dopiero teraz „byle nastepny etap" jest miejscem, do ktorego
      // uzytkownik ma wrocic, jesli zamknie odtwarzacz.
      zapiszPostep(this.props.userId, this.props.trainingId, {
        stepIndex: stepIndex + 1,
        answersByStep,
      });
      this.setState({
        isLoading: false,
        stepIndex: stepIndex + 1,
        phase: PHASE_MATERIAL,
        currentQuestionIndex: 0,
        currentStepAnswers: {},
        stepResult: null
      });
    }
  };

  advanceAfterLastStep = () => {
    const { courseData } = this.props;
    const postTestMaterial = courseData && courseData.postTestMaterial;
    if (postTestMaterial && postTestMaterial.url) {
      this.setState({ phase: PHASE_POST_TEST_MATERIAL });
    } else {
      this.submitScenario();
    }
  };

  handlePostTestMaterialDone = () => {
    this.submitScenario();
  };

  submitScenario = async () => {
    const { trainingId, userId, isPreview } = this.props;
    const { answersByStep } = this.state;
    trackTrainingEvent('scenario_submit', 'scenario-submit', 'Scenario submitted', {
      trainingId,
      stepsCount: Object.keys(answersByStep).length,
      isPreview,
    });
    this.setState({ isLoading: true, error: null });

    try {
      // Preview mode evaluates every step's answers WITHOUT recording anything
      // (no user, no saved result, no attendance-list join) — mirrors Exam.js.
      const endpoint = isPreview ? 'preview-submit-scenario' : 'submit-scenario';
      const payload = isPreview ? { answersByStep } : { userId, answersByStep };
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}/${trainingId}`,
        payload
      );
      const { t } = this.props;
      const { error, message, data, nextTraining } = response.data;

      // Per-step results already showed each step's points as it was passed —
      // the final scenario message is a plain pass/fail notice, not another
      // score readout (the backend reuses the classic exam's "X/Y points"
      // wording here, which does not apply to a scenario and reads as a
      // confusing total, e.g. 13/8).
      if (!error) {
        // Scenariusz zaliczony i zapisany na serwerze — lokalny postep nie jest juz
        // do niczego potrzebny i przy kolejnym wejsciu tylko myliłby uzytkownika
        // propozycja „kontynuuj".
        wyczyscPostep(this.props.userId, trainingId);
      }

      this.setState({
        isLoading: false,
        phase: PHASE_DONE,
        showThankYouModal: true,
        modalMessage: error ? t(message, data) : t('training_passed'),
        isPassed: !error,
        nextTraining: nextTraining ?? null
      });
      trackTrainingEvent('scenario_result', 'scenario-result', 'Scenario result', {
        trainingId,
        passed: !error,
        message,
        nextTraining: nextTraining ?? null,
        isPreview,
      });

      const { returnUrl } = this.props;
      // Not in preview — the preview tab is closed on confirm instead (see
      // handleThankYouModalClose).
      if (!isPreview && !error && !nextTraining && returnUrl) {
        setTimeout(() => { window.location.href = returnUrl; }, 3000);
      }
    } catch (err) {
      trackTrainingEvent('scenario_error', 'scenario-error', 'Scenario error', {
        trainingId,
        message: err.message,
        status: err.response?.status || null,
      });
      this.setState({ isLoading: false, error: err });
    }
  };

  handleThankYouModalClose = () => {
    const { nextTraining } = this.state;
    const { returnUrl, onRetakeCourse, startNewTraining, isPreview } = this.props;

    if (isPreview) {
      // Preview runs in a new tab opened from the panel — closing it returns the
      // creator to the system. Fall back to returnUrl/reload if close is blocked.
      window.close();
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.location.reload();
      }
      return;
    }

    if (!nextTraining) {
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.location.reload();
      }
    } else {
      this.setState({ showThankYouModal: false });
      onRetakeCourse();
      startNewTraining(nextTraining);
    }
  };

  handleCloseErrorModal = () => {
    this.setState({ error: null });
  };

  renderMaterial = (material, type, onDone, isLastMaterial) => {
    if (!material || !material.url) {
      return null;
    }
    if (type === 'video') {
      return (
        <VideoContent
          key={`material-${type}-${this.state.phase}-${this.state.stepIndex}`}
          content={material}
          onExamStart={onDone}
          isLastMaterial={isLastMaterial}
          startSeconds={material.startSeconds}
          endSeconds={material.endSeconds}
          showSeekControls={this.props.courseData && this.props.courseData.showSeekControls}
        />
      );
    }
    return (
      <PDFCourseContent
        key={`material-${type}-${this.state.phase}-${this.state.stepIndex}`}
        content={material}
        onExamStart={onDone}
        isLastMaterial={isLastMaterial}
        pageFrom={material.pageFrom}
        pageTo={material.pageTo}
      />
    );
  };

  render() {
    const { t } = this.props;
    const {
      phase, stepIndex, currentQuestionIndex, isLoading, error,
      stepResult, showThankYouModal, modalMessage, isPassed, nextTraining, wznowienie
    } = this.state;
    const steps = this.getSteps();
    const step = this.getCurrentStep();
    const { courseData } = this.props;

    return (
      <div className="ScenarioTraining d-flex flex-column h-100">
        <LoadingSpinner isLoading={isLoading} />

        {/* Where am I in the scenario. Until now the stage number surfaced only
            in the failed-stage modal, so a learner walking a multi-stage training
            had no idea how many stages were left. Fixed badge so it cannot
            disturb the full-height PDF/video players. */}
        {steps.length > 1 && (phase === PHASE_MATERIAL || phase === PHASE_QUESTIONS) && (
          <div
            className="position-fixed top-0 end-0 m-3 px-3 py-1 rounded-pill"
            style={{ zIndex: 1030, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '13px', fontWeight: 600 }}
          >
            {t('scenario_step_progress', { step: stepIndex + 1, total: steps.length })}
          </div>
        )}

        {phase === PHASE_MATERIAL && step && (
          <div className="flex-grow-1">
            {this.renderMaterial(step.material, step.type, this.handleMaterialDone, stepIndex >= steps.length - 1)}
          </div>
        )}

        {phase === PHASE_QUESTIONS && step && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="col">
              {currentQuestionIndex < step.questions.length && (
                <Question
                  key={step.questions[currentQuestionIndex].id}
                  question={step.questions[currentQuestionIndex].question}
                  answers={step.questions[currentQuestionIndex].answers}
                  onAnswerSubmit={this.handleAnswerSubmit}
                  questionId={step.questions[currentQuestionIndex].id}
                  imageUrl={step.questions[currentQuestionIndex].image_url}
                />
              )}
            </div>
          </div>
        )}

        {phase === PHASE_STEP_RESULT && stepResult && (
          <Modal show={true} backdrop="static" keyboard={false}>
            <Modal.Header>
              <Modal.Title>
                {t(stepResult.passed ? 'scenario_step_passed_title' : 'scenario_step_failed_title', { step: (stepIndex + 1) })}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{t(stepResult.message, stepResult.data)}</p>
            </Modal.Body>
            <Modal.Footer>
              {stepResult.passed ? (
                <Button onClick={this.handleStepResultContinue}>
                  {t('scenario_continue_step')}
                </Button>
              ) : (
                <Button variant="dark" onClick={this.handleRetryStep}>
                  {t('scenario_retry_step')}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        )}

        {phase === PHASE_POST_TEST_MATERIAL && courseData && courseData.postTestMaterial && (
          <div className="flex-grow-1">
            {this.renderMaterial(
              courseData.postTestMaterial,
              courseData.postTestMaterial.type,
              this.handlePostTestMaterialDone,
              true
            )}
          </div>
        )}

        {/* Propozycja wznowienia — pokazywana ZANIM uzytkownik zobaczy pierwszy etap,
            zeby nie zaczynal od zera szkolenia, ktore juz w czesci przeszedl.
            Niezamykalna: jedynym wyjsciem jest swiadomy wybor jednej z dwoch opcji. */}
        <Modal show={!!wznowienie} backdrop="static" keyboard={false} centered>
          <Modal.Header>
            <Modal.Title>{t('training_left_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {t('training_resume_description', {
                step: (wznowienie?.stepIndex ?? 0) + 1,
                total: this.getSteps().length,
              })}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={this.zacznijOdPoczatku}>
              {t('start_from_beginning')}
            </Button>
            <Button variant="danger" onClick={this.kontynuujOdZapisanego}>
              {t('continue_training')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Wynik szkolenia. BEZ przycisku zamkniecia i bez `onHide`: modal z krzyzykiem
            i tytulem „Dziekujemy" — pokazywanym tez po NIEZALICZENIU — sprawial, ze ludzie
            wychodzili przekonani, ze maja zaliczone (zgloszenie amwar162 2026-08-11:
            „nawet dobrze nie zaczal, a system mu zaliczyl"; w bazie nie bylo zadnego wpisu).
            Tytul mowi teraz wprost, jaki jest wynik, a wyjscie prowadzi tylko przez decyzje. */}
        <Modal show={showThankYouModal} backdrop="static" keyboard={false} centered>
          <Modal.Header>
            <Modal.Title>{isPassed ? t('training_passed_title') : t('training_not_passed_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{modalMessage}</p>
          </Modal.Body>
          <Modal.Footer>
            {isPassed ? (
              <Button onClick={this.handleThankYouModalClose}>
                {nextTraining === null ? t('finish_session') : t('do_next_exam')}
              </Button>
            ) : (
              <Button variant="dark" onClick={() => this.props.onRetakeCourse()}>
                {t('retake_exam')}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {error && (
          <Modal show={true} onHide={this.handleCloseErrorModal}>
            <Modal.Header closeButton>
              <Modal.Title>{t('error')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{error.message || 'An unexpected error occurred.'}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="danger" onClick={this.handleCloseErrorModal}>
                {t('close')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    );
  }
}

export default withTranslation()(ScenarioTraining);
